import { Injectable, Signal, computed, effect, inject, signal } from '@angular/core';
import { QuizChatMessage } from '@services/quiz-chat-service';
import { QuizEngine, QuizPhotoPair, QuizPhotoSlot } from '@services/consultation-quiz-engine-service/consultation-quiz-engine.types';
import { InstantCheckupService } from '@services/instant-checkup-service';
import { ConnectionService } from '@services/connection-service';
import { CommonUtilityHelper } from '@services/common-utility-helper/common-utility-helper';
import { AppConfig } from 'client/app/app.config';

/** A photo the user uploaded earlier, before the label and type the caller knows are attached. */
interface UploadedPhoto {
  canonicalUrl: string;
  checkupId: string;
  url: string;
}

type ChatMessageField = Parameters<QuizChatMessage['get']>[0];

const DISPLAY_URL_FIELDS: readonly ChatMessageField[] = ['tempCompressedImage', 'tempThumbnailUrl', 'tempUrl', 'Message'];
const CANONICAL_URL_FIELD: ChatMessageField = 'Message';

function readString(message: QuizChatMessage, field: ChatMessageField): string {
  const value: unknown = message.get(field);
  return typeof value === 'string' ? value : '';
}

/** Resolves the photo pair for a capture question. See client/components/quiz/CLAUDE.md. */
@Injectable()
export class QuizPhotoPairService {
  private readonly instantCheckupService = inject(InstantCheckupService);
  private readonly connectionService = inject(ConnectionService);
  private readonly commonUtilityHelper = inject(CommonUtilityHelper);
  private readonly appConfig = inject(AppConfig);

  private readonly engine = signal<QuizEngine | null>(null);
  private readonly currentUploadedPhoto = signal<UploadedPhoto | null>(null);
  private readonly alternateUploadedPhoto = signal<UploadedPhoto | null>(null);

  readonly currentCheckupType = computed(() => {
    const option = this.engine()?.photoCtaOption();
    if (!option) return '';
    try {
      return (JSON.parse(option.params?.queryParams || '{}') as { tag?: string }).tag || '';
    } catch {
      return '';
    }
  });
  private readonly alternateCheckupType = computed(
    () => this.instantCheckupService.getAlternatePhotoType(this.currentCheckupType()),
  );

  private readonly pairFromEngine: Signal<QuizPhotoPair | null> = computed(() => this.engine()?.photoPair?.() ?? null);
  readonly currentLabel: Signal<string> = computed(() => this.pairFromEngine()?.current?.label
    || this.instantCheckupService.getPhotoTypeLabel(this.currentCheckupType()));
  readonly alternateLabel: Signal<string> = computed(() => this.pairFromEngine()?.alternate?.label
    || this.instantCheckupService.getPhotoTypeLabel(this.alternateCheckupType()));

  readonly currentPhoto: Signal<QuizPhotoSlot | null> = computed(() => this.buildSlot(
    this.pairFromEngine()?.current ?? null, this.currentUploadedPhoto(), this.currentCheckupType(), this.currentLabel(),
  ));
  readonly alternatePhoto: Signal<QuizPhotoSlot | null> = computed(() => this.buildSlot(
    this.pairFromEngine()?.alternate ?? null, this.alternateUploadedPhoto(), this.alternateCheckupType(), this.alternateLabel(),
  ));

  readonly showPair: Signal<boolean> = computed(() => {
    const fromEngine = this.pairFromEngine();
    if (fromEngine) return fromEngine.showPair;
    return !!this.alternateUploadedPhoto() && this.alternateIsAskedFirst();
  });

  private readonly alternateIsAskedFirst: Signal<boolean> = computed(() => {
    const order = this.engine()?.questionOrder?.() || [];
    const current = order.indexOf(this.instantCheckupService.getPhotoQuestionId(this.currentCheckupType()));
    const alternate = order.indexOf(this.instantCheckupService.getPhotoQuestionId(this.alternateCheckupType()));
    if (current < 0 || alternate < 0) return true;
    return alternate < current;
  });

  constructor() {
    effect(() => {
      const engine = this.engine();
      if (!engine || this.pairFromEngine()) return;
      void this.refreshAlternatePhoto(this.alternateCheckupType(), this.readMessages(engine));
    });
    effect(() => {
      const engine = this.engine();
      if (!engine || this.pairFromEngine()) return;
      void this.refreshCurrentPhoto(this.currentCheckupType(), this.readMessages(engine));
    });
  }

  configure(engine: QuizEngine): void {
    this.engine.set(engine);
  }

  async resolveCheckupId(photo: QuizPhotoSlot): Promise<string> {
    if (photo.checkupId) return photo.checkupId;
    if (!photo.canonicalUrl) return '';
    const checkup = await this.connectionService.fetchInstantCheckupByImageUrl(
      this.connectionService.getActingUser(), photo.canonicalUrl,
    );
    return checkup?.id || '';
  }

  private buildSlot(
    fromEngine: QuizPhotoSlot | null, uploaded: UploadedPhoto | null, type: string, label: string,
  ): QuizPhotoSlot | null {
    if (fromEngine) return { ...fromEngine, type };
    return uploaded ? { ...uploaded, label, type } : null;
  }

  private readMessages(engine: QuizEngine): QuizChatMessage[] {
    return (engine.messages?.() || []) as QuizChatMessage[];
  }

  private async refreshAlternatePhoto(alternateType: string, messages: QuizChatMessage[]): Promise<void> {
    this.alternateUploadedPhoto.set(null);
    if (!alternateType) return;
    const chatPhoto = this.findPhotoInChatHistory(alternateType, messages);
    if (chatPhoto && await this.isDisplayableImage(chatPhoto.url)) {
      this.alternateUploadedPhoto.set(chatPhoto);
      return;
    }
    const recentPhoto = await this.findRecentlyUploadedPhoto(alternateType);
    if (recentPhoto && await this.isDisplayableImage(recentPhoto.url)) this.alternateUploadedPhoto.set(recentPhoto);
  }

  private async refreshCurrentPhoto(currentType: string, messages: QuizChatMessage[]): Promise<void> {
    this.currentUploadedPhoto.set(null);
    if (!currentType) return;
    const photo = this.findPhotoInChatHistory(currentType, messages) ?? await this.findRecentlyUploadedPhoto(currentType);
    if (photo && await this.isDisplayableImage(photo.url)) this.currentUploadedPhoto.set(photo);
  }

  private findPhotoInChatHistory(checkupType: string, messages: QuizChatMessage[]): UploadedPhoto | null {
    const questionId = this.instantCheckupService.getPhotoQuestionId(checkupType);
    const photoAnswer = messages.find(
      (message) => message.get('Owner') === this.appConfig.Shared.Assistant.Owner.USER
        && message.get('questionId') === questionId,
    );
    if (!photoAnswer) return null;
    const url = DISPLAY_URL_FIELDS.reduce((url, field) => url || readString(photoAnswer, field), '');
    if (!url) return null;
    return { canonicalUrl: readString(photoAnswer, CANONICAL_URL_FIELD) || url, checkupId: '', url };
  }

  private async findRecentlyUploadedPhoto(checkupType: string): Promise<UploadedPhoto | null> {
    const checkup = await this.instantCheckupService.getRecentInstantCheckupOfType(
      this.connectionService.getActingUser(), checkupType,
    );
    const url = checkup?.imagePath || '';
    return url ? { canonicalUrl: url, checkupId: checkup?.objectId || '', url } : null;
  }

  private async isDisplayableImage(url: string): Promise<boolean> {
    if (!url) return false;
    return !(await this.commonUtilityHelper.checkImageDeleted(url));
  }
}
